// kFileDownload — hand bytes (or text) to the browser as a saved file. ONE concern: the moment
// between "the app has the content" and "the content is in the user's Downloads folder".
//
// WHY IT EXISTS (rule 2 / rule 3). The eight-line anchor idiom below was hand-written FOUR times in
// this shared tree before anyone lifted it:
//     kExcelExport._OnBlobReady        blob  -> anchor -> click -> remove -> revoke
//     kPivotHostCommands.Download      text  -> anchor -> click -> remove -> revoke
//     kPowerAutomatePackage.Download   bytes -> anchor -> click -> remove -> revoke
//     kExportExcel.Prepare             data: url -> anchor -> click       <- and NEVER removes it
// The rule says lift on the SECOND copy. The fourth is what finally paid for it, and the fourth copy
// is also the one carrying a defect the other three do not: it appends an <a> to <body> on every
// export and leaves it there for the life of the page. That is the whole argument for one
// implementation — a bug fixed here is fixed for every consumer, and a bug in copy four is invisible
// to the three people maintaining copies one to three.
//
// ✅ ALL FOUR COPIES WERE MIGRATED ON 2026-08-18, on an explicit instruction. Each became a
// deletion, which is what the paragraph above predicted. Two of them were carrying defects that
// only became visible once they were read side by side:
//     kExportExcel.Prepare          leaked an <a> into <body> on EVERY export (removal on the
//                                   happy path). SaveBlob removes it in a `finally`.
//     kPowerAutomatePackage.Download built its Blob from `zipData.buffer` — the whole backing
//                                   store, not the view — so a subarray producer would have
//                                   written a .zip with bytes after the EOCD record.
// That is the argument for one implementation, stated as a measurement rather than as tidiness: a
// bug in copy four is invisible to the three people maintaining copies one to three.
//
// ⚠ THE REVOKE IS SYNCHRONOUS, AND THAT IS THE ESTATE'S MEASURED IDIOM RATHER THAN A GUESS. Three of
// the four copies above revoke immediately after `.click()` and all three ship inside SharePoint
// pages today. The download is STARTED synchronously while the click is being dispatched, so the url
// has already been resolved by the time it is revoked. Deferring the revoke on a timer would leave
// the object url — and therefore the whole file — alive in memory for an unbounded period, which is
// the failure this class is more likely to meet on a 10 MB document than a cancelled save.
import ks from "./kBrowserShared";
import { kBytesCore } from "../kReusableCoreTS/kBytesCore";

export class kFileDownload {
    /**
     * Save bytes as a file.
     *
     * ⚠ IT ANSWERS WHETHER IT ACTED, and the caller is expected to care. A silent `void` here would
     * make "the browser blocked it", "the name was empty" and "it worked" the same observation — and
     * a download is a user-visible promise, so a caller that closes a dialog or paints a confirmation
     * needs to know which of those happened.
     *
     * ⚠ AN EMPTY NAME IS REFUSED RATHER THAN DEFAULTED. A browser handed `download=""` saves the file
     * under a name of its own choosing (commonly the object-url GUID), so the user gets a file they
     * cannot identify and nothing anywhere says why. Naming is the CALLER's decision — it is the only
     * layer that knows what the bytes are — and this class must not invent one on its behalf.
     *
     * 🔴 IT SAVES THE VIEW'S OWN WINDOW, NEVER `Bytes.buffer`. A `Uint8Array` is frequently a WINDOW
     * over a larger buffer — `kMimeBodyCore.Base64ToBytes` over-allocates and returns
     * `Out.subarray(0, Written)`, so its buffer is longer than the file by the padding plus every line
     * break it skipped. Handing `.buffer` to the Blob appends those bytes to the saved file: measured
     * 2026-08-18 on a wrapped-base64 part, 22 real bytes and 29 reaching the Blob. The result opens
     * for a `.txt` and corrupts a zip-shaped one (`.docx`, `.xlsx`), SILENTLY, because the file is
     * longer rather than absent.
     *
     * ⚠ THE FIRST CALLER WAS SAFE BY LUCK AND THE SECOND IS NOT, which is why the copy is here and not
     * at a call site. `kFilePreviewRender.DecodeBytes` allocates exactly and views the whole buffer, so
     * the preview download was correct; an email attachment decoded from MIME is a window, so it was
     * not. A rule that holds "for the callers we happen to have" is not a rule.
     *
     * ⚠ THE RULE MOVED TO `kBytesCore.ExactBuffer` ON 2026-08-18 and this delegates to it. Two
     * reasons, and the first is not tidiness: a sweep found the SAME `.buffer` expression at six
     * more sites across the PDF classes, so the question needed ONE place to be asked. The second is
     * that the helper copies only when the view really is a window, where this method copied every
     * time — so the ordinary exactly-sized caller now allocates nothing at all.
     */
    public static SaveBytes = (Bytes: Uint8Array, FileName: string, ContentType: string): boolean => {
        if (ks.KErrorMode === true) { return false; }
        try {
            if (FileName === "") { return false; }
            const Exact: ArrayBuffer = kBytesCore.ExactBuffer(Bytes);
            return kFileDownload.SaveBlob(new Blob([Exact], { type: ContentType }), FileName);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kFileDownload.SaveBytes");
            return false;
        }
    };

    /** Save text as a file. `ContentType` is defaulted so the common case needs no argument. */
    public static SaveText = (Text: string, FileName: string,
        ContentType: string = "text/plain;charset=utf-8"): boolean => {
        if (ks.KErrorMode === true) { return false; }
        try {
            if (FileName === "") { return false; }
            return kFileDownload.SaveBlob(new Blob([Text], { type: ContentType }), FileName);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kFileDownload.SaveText");
            return false;
        }
    };

    /**
     * Open a Blob in a new browser tab (owner 2026-10-29: "Show file"); MimeType "" = the Blob's own type. The object
     * URL is revoked after a minute (the exception to the synchronous revoke: the new tab must read the URL first).
     * false = the browser blocked the tab (a pop-up blocker).
     */
    public static OpenBlobInTab = (Content: Blob, MimeType: string = ""): boolean => {
        if (ks.KErrorMode === true) { return false; }
        try {
            let Payload: Blob = Content;
            if (MimeType !== "") { Payload = new Blob([Content], { type: MimeType }); }
            const Url: string = URL.createObjectURL(Payload);
            const Tab: Window | null = window.open(Url, "_blank");
            window.setTimeout(kFileDownload.RevokeLater(Url), 60000);
            return Tab !== null;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kFileDownload.OpenBlobInTab");
            return false;
        }
    };

    /** The timer callback that frees one object URL. */
    public static RevokeLater = (Url: string): (() => void) => {
        return (): void => {
            if (ks.KErrorMode === true) { return; }
            try {
                URL.revokeObjectURL(Url);
            } catch (e) {
                ks.KGlobalErrorHandler(e, "kFileDownload.RevokeLater");
            }
        };
    };

    /**
     * The one place the anchor exists.
     *
     * ⚠ THE ANCHOR IS REMOVED IN A `finally`. Copy four leaks one per export because its removal sits
     * on the happy path; a click handler that throws would leak here too without this.
     */
    public static SaveBlob = (Payload: Blob, FileName: string): boolean => {
        if (ks.KErrorMode === true) { return false; }
        try {
            if (FileName === "") { return false; }
            const Url: string = URL.createObjectURL(Payload);
            const Link: HTMLAnchorElement = document.createElement("a");
            Link.href = Url;
            Link.download = FileName;
            // OFF-SCREEN RATHER THAN UNAPPENDED: Firefox will not act on a click dispatched at an
            // anchor that is not in the document, and a visible one would flash a stray link into the
            // page for a frame.
            Link.style.display = "none";
            document.body.appendChild(Link);
            try {
                Link.click();
            } finally {
                document.body.removeChild(Link);
                URL.revokeObjectURL(Url);
            }
            return true;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kFileDownload.SaveBlob");
            return false;
        }
    };

    /**
     * Save the file at a URL, WITHOUT the bytes ever entering this page.
     *
     * 🎯 THIS IS THE ONLY SAVE WITH NO SIZE CEILING, and that is the whole reason it exists beside
     * `SaveBytes`. Every other method here starts from content the app is already holding, so the
     * file has already crossed the app's own service, been base64'd into a JSON response, and been
     * decoded into a Blob — three places with real limits, on top of whatever request timeout sits
     * in between. Here the browser fetches from the origin server itself and streams straight to
     * disk: this page never sees a byte, so a 4 GB file costs exactly what a 4 KB one costs.
     *
     * ⚠ `download` IS IGNORED CROSS-ORIGIN, AND THE SERVER'S HEADER IS WHAT NAMES THE FILE. The
     * attribute is set anyway — it is honoured for a same-origin url and costs nothing — but for the
     * cross-origin case the browser uses `Content-Disposition` and nothing a caller does here can
     * change that. So a URL handed to this method MUST be one the server serves as
     * `Content-Disposition: attachment; filename="…"`. Measured 2026-08-19 against Graph's
     * `@microsoft.graph.downloadUrl`, which does exactly that (`filename*=utf-8''…` plus the ASCII
     * fallback). ⛔ A url served INLINE will NAVIGATE THE PAGE AWAY instead of downloading — the
     * user loses whatever they were doing. If you cannot say what the server sends, fetch the bytes
     * and use `SaveBytes`.
     *
     * ⚠ NO `target="_blank"`. It would open a tab that instantly closes itself once the download
     * starts, which reads as a popup and is what popup blockers act on.
     *
     * ⚠ `rel="noopener noreferrer"`: the url is very often a CAPABILITY (a pre-authenticated,
     * time-limited link). `noreferrer` stops it being handed to the destination as a Referer, and
     * `noopener` is the standing rule for any anchor this estate creates.
     *
     * ⚠ NOTHING IS REVOKED, because nothing was minted. Unlike `SaveBlob` there is no object url and
     * no retained buffer — which is also why this cannot report whether the file actually arrived.
     * It answers whether the CLICK WAS DISPATCHED, exactly as the others do.
     *
     * ⚠ `Interception = false` ON A SHAREPOINT ONLINE PAGE. The modern page's router listens for
     * anchor clicks on the document and takes the navigation over itself unless the anchor carries
     * `data-interception="off"` (the documented SPFx opt-out; kFileCells measured it ignoring
     * `target`). A download anchor must never be routed, so a caller running inside a SharePoint page
     * passes false. Inert anywhere else. Defaulted to true so existing callers are unchanged.
     */
    public static SaveFromUrl = (Url: string, FileName: string, Interception: boolean = true): boolean => {
        if (ks.KErrorMode === true) { return false; }
        try {
            if (Url === "") { return false; }
            if (FileName === "") { return false; }
            const Link: HTMLAnchorElement = document.createElement("a");
            Link.href = Url;
            Link.download = FileName;
            Link.rel = "noopener noreferrer";
            if (Interception === false) { Link.setAttribute("data-interception", "off"); }
            // OFF-SCREEN RATHER THAN UNAPPENDED — same reason as SaveBlob: Firefox will not act on a
            // click dispatched at an anchor that is not in the document.
            Link.style.display = "none";
            document.body.appendChild(Link);
            try {
                Link.click();
            } finally {
                document.body.removeChild(Link);
            }
            return true;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kFileDownload.SaveFromUrl");
            return false;
        }
    };

    /**
     * Make a string safe to be a file NAME — not a path.
     *
     * ⚠ THIS IS NOT DECORATION, AND MOSE SUPPLIES THE COUNTEREXAMPLE. A downloaded item is not always
     * named by a file name: an email is named by its SUBJECT, and a subject legitimately contains
     * `/`, `\` and `:`. "Re: Q3/Q4 planning" handed to `download=` is a name a browser will mangle or
     * reject, and the same string has already fabricated a folder in this estate's own results tree.
     *
     * ⚠ IT ANSWERS "" FOR A NAME THAT SANITISES TO NOTHING, and the caller must handle that rather
     * than receive a plausible invention. A subject of "///" is not a file called "___".
     */
    /** The longest name SafeName answers (characters). */
    public static readonly MaxNameLength: number = 120;

    public static SafeName = (Name: string): string => {
        if (ks.KErrorMode === true) { return ""; }
        try {
            // The Windows-reserved set plus the separators, and the C0 control range — a newline in a
            // name is accepted by some browsers and truncates the name in others.
            let Out: string = "";
            for (const Ch of Name) {
                const Code: number = Ch.charCodeAt(0);
                if (Code < 32 || (Code >= 127 && Code <= 159)) { continue; }
                // bidirectional overrides / isolates and zero-width characters spoof a name (U+202E makes "gpj.exe" show as "exe.jpg")
                if ((Code >= 0x202A && Code <= 0x202E) || (Code >= 0x2066 && Code <= 0x2069) || (Code >= 0x200B && Code <= 0x200F) || Code === 0xFEFF) { continue; }
                if ("/\\:*?\"<>|".indexOf(Ch) > -1) { continue; }
                Out = Out + Ch;
            }
            // A LEADING DOT WOULD MAKE A HIDDEN FILE, and trailing dots and spaces are silently
            // stripped by Windows, which turns "report." into "report" without telling anyone.
            Out = Out.replace(/^[.\s]+/, "").replace(/[.\s]+$/, "");
            // Windows cannot create CON, NUL, COM1 ... (with or without an extension): prefix an underscore. A name over MaxNameLength is cut (browsers and Windows refuse 255).
            if (/^(con|prn|aux|nul|com[1-9]|lpt[1-9])(\..*)?$/i.test(Out)) { Out = "_" + Out; }
            if (Out.length > kFileDownload.MaxNameLength) { Out = Out.substring(0, kFileDownload.MaxNameLength).replace(/[.\s]+$/, ""); }
            return Out;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kFileDownload.SafeName");
            return "";
        }
    };
}
